import {filter} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import type {EagColumn} from '../../../../../types/EagColumn';
import type {EagColumnOrGroup} from '../../../../../types/EagColumnGroup';
import {isEagColumnGroup} from '../../../../../utils/isEagColumnGroup';

/**
 * Resolves the list of filterable columns for the selected category.
 *
 * Category resolution:
 * - null → []
 * - '__standalone__' → top-level non-group columns with filter defined
 * - groupId → children of all group instances with matching groupId, deduped by key
 *
 * When searchTerm is non-empty, results are filtered by headerName (case-insensitive).
 */
export const getVisibleFilterColumns = (
  columnConfig: EagColumnOrGroup[],
  selectedCategoryId: string | null,
  searchTerm: string
): EagColumn[] => {
  if (!selectedCategoryId) {
    return [];
  }

  let filterableColumns: EagColumn[];

  if (selectedCategoryId === '__standalone__') {
    filterableColumns = [];
    for (const item of columnConfig) {
      if (!isEagColumnGroup(item) && item.filter) {
        filterableColumns.push(item);
      }
    }
  } else {
    const allChildren: EagColumn[] = [];
    for (const item of columnConfig) {
      if (isEagColumnGroup(item) && item.groupId === selectedCategoryId) {
        allChildren.push(...filter((col: EagColumn) => isNotNil(col.filter), item.children));
      }
    }
    const seenKeys = new Set<string>();
    filterableColumns = allChildren.filter((col) => {
      if (seenKeys.has(col.key)) {
        return false;
      }
      seenKeys.add(col.key);
      return true;
    });
  }

  if (!searchTerm) {
    return filterableColumns;
  }

  const lowerSearch = searchTerm.toLowerCase();
  return filterableColumns.filter((col) => col.headerName.toLowerCase().includes(lowerSearch));
};
